import { Box, SxProps } from "@mui/material";
import { MouseEvent, ReactNode, useCallback } from "react";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { useCustomerNavigation } from "../../../hooks/useCustomerNavigation";
import { EntriesGroupAndSortKey } from "../constants";
import { Entry } from "../entriesAPI";
import { EntriesGroupLabel } from "../styles";
import { getName } from "../../../common/helpers/common";

type Props = {
  label: ReactNode;
  groupData?: Entry[];
  isFirstGroupingLevel?: boolean;
  sx?: SxProps;
};

export const EntriesGroupHeader = ({
  label,
  groupData,
  isFirstGroupingLevel = false,
  sx,
}: Props) => {
  const { navigateToCustomer, isNavigationLoading } = useCustomerNavigation();

  const findFirstEntry = useCallback((data: any): Entry | null => {
    if (Array.isArray(data) && data.length > 0) {
      return data[0];
    }
    if (typeof data === "object" && data !== null) {
      for (const value of Object.values(data)) {
        const entry = findFirstEntry(value);
        if (entry) return entry;
      }
    }
    return null;
  }, []);

  const getNavigationData = useCallback(() => {
    const entry = findFirstEntry(groupData);
    if (!entry) return null;

    if (entry.customerOrgName === label && entry.customerOrgId) {
      return { type: EntriesGroupAndSortKey.CustomerOrg, entry };
    }

    const talentName = getName(entry);
    if (talentName === label && entry.talentId) {
      return { type: EntriesGroupAndSortKey.Talent, entry };
    }

    return null;
  }, [groupData, label, findFirstEntry]);

  const navigationData = getNavigationData();

  const handleCustomerClick = useCallback(async () => {
    if (
      !navigationData ||
      navigationData.type !== EntriesGroupAndSortKey.CustomerOrg
    )
      return;

    const entry = navigationData.entry;
    await navigateToCustomer(entry.customerOrgId);
  }, [navigationData, navigateToCustomer]);

  const handleTalentClick = useCallback(() => {
    if (
      !navigationData ||
      navigationData.type !== EntriesGroupAndSortKey.Talent
    )
      return;

    const entry = navigationData.entry;
    window.open(`/home/talents/${entry.talentId}/profile`, "_blank");
  }, [navigationData]);

  const content = (
    <EntriesGroupLabel
      variant="header"
      sx={{
        ...(!isFirstGroupingLevel && { marginLeft: "8px" }),
        ...(sx || {}),
        ...(navigationData && {
          cursor: "pointer",
        }),
      }}
    >
      {label}
    </EntriesGroupLabel>
  );

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (navigationData?.type === EntriesGroupAndSortKey.CustomerOrg) {
      void handleCustomerClick();
    } else if (navigationData?.type === EntriesGroupAndSortKey.Talent) {
      handleTalentClick();
    }
  };

  const onAuxClick = (event: MouseEvent<HTMLDivElement>) => {
    // Middle mouse button (wheel click)
    if (event.button === 1) {
      event.preventDefault();
      onClick(event);
    }
  };

  const isClickableLink = !!navigationData;

  if (isClickableLink) {
    return (
      <>
        <OverlaySpinner isLoading={isNavigationLoading} />
        <Box
          onClick={onClick}
          onAuxClick={onAuxClick}
          sx={{
            textDecoration: "none",
            color: "primary.main",
            cursor: "pointer",
          }}
        >
          {content}
        </Box>
      </>
    );
  }

  return content;
};

export default EntriesGroupHeader;
